import { type Attributes, SpanStatusCode, trace } from '@opentelemetry/api'

import type { GlobalRequestOptions } from './types'

const tracer = trace.getTracer('commerce')

// NEVER put access tokens, raw GraphQL queries/variables, customer identifiers,
// emails, names, addresses or cart line contents in span attributes.
export type ShopifyAttributes = {
  'shopify.api': 'storefront' | 'admin'
  'shopify.operation':
    | 'products'
    | 'collection'
    | 'cart'
    | 'checkout'
    | 'customer'
    | 'metaobject'
    | 'product-sync'
    | 'inventory-sync'
    | 'webhook'
  'commerce.channel'?: 'web'
} & Attributes

export async function tracedShopifyCall<T>(
  spanName: string,
  attributes: ShopifyAttributes,
  fn: () => Promise<T>,
): Promise<T> {
  return tracer.startActiveSpan(spanName, async span => {
    try {
      span.setAttributes({
        'external.system': 'shopify',
        'commerce.channel': 'web',
        ...attributes,
      })

      const result = await fn()
      span.setStatus({ code: SpanStatusCode.OK })
      return result
    } catch (error) {
      span.recordException(error as Error)
      span.setStatus({
        code: SpanStatusCode.ERROR,
        message: error instanceof Error ? error.message : 'Unknown error',
      })
      throw error
    } finally {
      span.end()
    }
  })
}

export const traceHandle =
  <A extends unknown[], R>(
    spanName: string,
    attributes: ShopifyAttributes,
    factory: (options: GlobalRequestOptions) => (...args: A) => Promise<R>,
  ) =>
  (options: GlobalRequestOptions) =>
  (...args: A): Promise<R> =>
    tracedShopifyCall(spanName, attributes, () => factory(options)(...args))
